// Report Issue Modal Component
// Modal for users to report issues with class documentation

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  className: string;
}

type IssueType = 'incorrect_info' | 'missing_info' | 'broken_link' | 'typo' | 'other';

export const ReportIssueModal: React.FC<ReportIssueModalProps> = ({
  isOpen,
  onClose,
  className
}) => {
  const { isAuthenticated } = useAuth();
  const [issueType, setIssueType] = useState<IssueType>('incorrect_info');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (!isAuthenticated) {
        setError('You must be logged in to report an issue');
        setIsSubmitting(false);
        return;
      }

      const response = await fetch('/api/issues/submit', {
        method: 'POST',
        credentials: 'include', // Send cookies automatically
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          class_name: className,
          issue_type: issueType,
          title,
          description
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit issue');
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setTitle('');
        setDescription('');
        setIssueType('incorrect_info');
        setSuccess(false);
      }, 2000);

    } catch (err: any) {
      setError(err.message || 'Failed to submit issue');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--ifm-background-color)',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {success ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ color: 'var(--ifm-color-success)', marginBottom: '0.5rem' }}>
              Issue Reported Successfully!
            </h2>
            <p style={{ color: 'var(--ifm-color-emphasis-700)' }}>
              Thank you for helping improve the documentation.
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, color: 'var(--ifm-font-color-base)' }}>
                🐛 Report Issue
              </h2>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--ifm-color-emphasis-600)',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--ifm-color-emphasis-100)', borderRadius: '6px' }}>
              <strong>Class:</strong> {className}
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor="issue-type"
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: 'var(--ifm-font-color-base)'
                  }}
                >
                  Issue Type *
                </label>
                <select
                  id="issue-type"
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value as IssueType)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.95rem',
                    border: '1px solid var(--ifm-color-emphasis-300)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--ifm-background-color)',
                    color: 'var(--ifm-font-color-base)'
                  }}
                >
                  <option value="incorrect_info">Incorrect Information</option>
                  <option value="missing_info">Missing Information</option>
                  <option value="broken_link">Broken Link</option>
                  <option value="typo">Typo/Grammar</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor="title"
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: 'var(--ifm-font-color-base)'
                  }}
                >
                  Title *
                </label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder="Brief description of the issue"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.95rem',
                    border: '1px solid var(--ifm-color-emphasis-300)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--ifm-background-color)',
                    color: 'var(--ifm-font-color-base)'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor="description"
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: 'var(--ifm-font-color-base)'
                  }}
                >
                  Description *
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  rows={6}
                  placeholder="Provide details about the issue, what's wrong, and how it should be corrected..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.95rem',
                    border: '1px solid var(--ifm-color-emphasis-300)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--ifm-background-color)',
                    color: 'var(--ifm-font-color-base)',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              {error && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: 'rgba(220, 53, 69, 0.1)',
                  border: '1px solid rgba(220, 53, 69, 0.3)',
                  borderRadius: '6px',
                  color: '#dc3545',
                  marginBottom: '1rem',
                  fontSize: '0.9rem'
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: '0.75rem 1.5rem',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    border: '1px solid var(--ifm-color-emphasis-300)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--ifm-background-color)',
                    color: 'var(--ifm-font-color-base)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: isSubmitting ? 'var(--ifm-color-emphasis-300)' : 'var(--ifm-color-primary)',
                    color: 'white',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Issue'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
